<template>
  <div class="checklist-inspection-form-container">
    <div class="breadcrumbs-container">
      <Breadcrumbs :breadcrumbsList="props.breadcrumbs" />
    </div>
    <div class="checklist-inspection-form">
      <p class="checklist-inspection-form-title">
        Readiness Inspection Checklist
      </p>
    </div>
    <div class="checklist-inspection-form-content">
      <ChecklistInspectionFormHeader
        :employee="props.employee"
        :responsibility="props.responsibility"
        :dot="props.dot"
        :currentBadge="props.currentBadge"
        :readyForLevel="props.readyForLevel"
        @badge-selected="handleBadgeSelected"
      />
    </div>
    <div class="checklist-inspection-table-content">
      <ChecklistInspectionTable 
        :inspectionData="props.inspectionData"
        :filterText="props.filterText"
        :showNoRecords="props.showNoRecords"
        :showPagination="props.showPagination"
        :pagination="props.pagination"
        :chipText="props.chipText"
        :filterGroups="props.filterGroups"
        :filterType="props.filterType"
        :statusOptions="props.statusOptions"
        :groupsItems="props.groupsItems"
        :levelCounter="props.levelCounter"
        @onSearch="handleSearch"
        @onClick="handleClick"
        @removeFilter="handleRemoveFilter"
        @filterTypes="handleFilterTypes"
        @onPaginationRequest="handlePaginationRequest"
        @clearSelection="handleClearSelection"
        @onCheckedItemsChanged="handleCheckedItemsChanged"
        @onPass="handlePass"
        @onFail="handleFail"
        @onNotApplicable="handleNotApplicable"
        @onClose="handleClose"
      />
    </div>
  </div>
</template>

<script setup>
import Breadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import ChecklistInspectionFormHeader from "@components/ChecklistInspectionFormHeader/ChecklistInspectionFormHeader.vue";
import ChecklistInspectionTable from "@components/ChecklistInspectionTable/ChecklistInspectionTable.vue";

const props = defineProps({
  breadcrumbs: {
    type: Array,
    default: () => [
      {
        text: "Your Employee to Inspect",
        href: "/my-stuff/employee",
      },
      {
        text: "Inspection",
        href: "/checklist-inspection-form",
        disabled: true,
      },
    ],
  },
  employee: {
    type: Object,
    default: () => ({
      id: null,
      firstName: "John",
      lastName: "Doe",
      position: "Senior Software Engineer",
      photoUrl: "",
      currentEmployeeBadge: null, // Badge object with id, level, status, is_current, etc.
    }),
  },
  responsibility: {
    type: Object,
    default: () => ({
      responsibilityCode: "#9999",
      responsibilityName: "Ensure Quality Standards",
      roleCode: "#245",
      roleName: "Implement CAPEX initiatives",
      positionCode: "#4567",
      positionName: "General Manager",
      responsibilityImportance: 3,
      roleImportance: 2,
      positionImportance: 1,
      positionCommitment: "Primary",
      assignedOn: new Date("2025-09-01"),
      newDuration: 15,
      show_codes: true,
    }),
  },
  dot: {
    type: String,
    default: "Good Standing",
  },
  currentBadge: {
    type: String,
    default: "Apprentice",
  },
  readyForLevel: {
    type: String,
    default: null,
  },
  inspectionData: {
    type: Array,
    default: () => [],
  },
  filterText: {
    type: String,
    default: "",
  },
  showNoRecords: {
    type: Boolean,
    default: false,
  },
  showPagination: {
    type: Boolean,
    default: true,
  },
  pagination: {
    type: Object,
    default: () => ({
      total: 0,
      page: 1,
      perPage: 10,
    }),
  },
  chipText: {
    type: Array,
    default: () => [],
  },
  filterGroups: {
    type: Array,
    default: () => [
      {
        label: "Status",
        items: [
          { label: "No Filter", value: "No Filter" },
          { label: "Not Yet Passed", value: "Not Yet Passed" },
          { label: "Only Failed", value: "Only Failed" },
          { label: "Only Not Yet Evaluated", value: "Only Not Yet Evaluated" },
          { label: "Only Reset", value: "Only Reset" },
          { label: "Only Not Applicable", value: "Only Not Applicable" },
          { label: "Only Passed", value: "Only Passed" },
        ],
      },
    ],
  },
  filterType: {
    type: String,
    default: "Checkpoint",
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  groupsItems: {
    type: Array,
    default: () => [],
  },
  levelCounter: {
    type: Object,
    default: () => ({
      Apprentice: 0,
      Professional: 0,
      Coach: 0,
      Master: 0,
    }),
  },
});

// Define emits
const emit = defineEmits([
  "onSearch",
  "onClick",
  "removeFilter",
  "filterTypes",
  "onPaginationRequest",
  "clearSelection",
  "onCheckedItemsChanged",
  "onPass",
  "onFail",
  "onNotApplicable",
  "onClose",
  "badge-selected",
]);

// Event handlers
const handleSearch = (searchValue) => {
  emit("onSearch", searchValue);
};

const handleClick = (action) => {
  emit("onClick", action);
};

const handleRemoveFilter = (filter) => {
  emit("removeFilter", filter);
};

const handleFilterTypes = (filterTypes) => {
  emit("filterTypes", filterTypes);
};

const handlePaginationRequest = (paginationData) => {
  emit("onPaginationRequest", paginationData);
};

const handleClearSelection = (selection) => {
  emit("clearSelection", selection);
};

const handleCheckedItemsChanged = (checkedItems) => {
  emit("onCheckedItemsChanged", checkedItems);
};

const handlePass = (checkpointData) => {
  emit("onPass", checkpointData);
};

const handleFail = (checkpointData) => {
  emit("onFail", checkpointData);
};

const handleNotApplicable = (checkpointData) => {
  emit("onNotApplicable", checkpointData);
};

const handleClose = (checkpointData) => {
  emit("onClose", checkpointData);
};

const handleBadgeSelected = (badgeData) => {
  emit("badge-selected", badgeData);
};
</script>

<style scoped>
.checklist-inspection-form-container {
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--Gray-200, #eaecf0);
  background: var(--White, #fff);
  box-shadow: 0 1px 3px 0 rgba(16, 24, 40, 0.1),
    0 1px 2px 0 rgba(16, 24, 40, 0.06);
}

.breadcrumbs-container {
  margin-left: 20px;
  margin-top: 12px;
}

.checklist-inspection-form {
  display: flex;
  height: 59px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  background-color: #d9d9d9;
  width: 100%;
  margin: 20px 0px;
}

.checklist-inspection-form-title {
  color: #101828;
  text-align: center;
  font-family: Inter;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  width: 100%;
  margin-bottom: 0px;
}

::v-deep(.checklist-inspection-form-header) {
  padding: 16px;
}

.checklist-inspection-table-content {
  margin-top: 10px;
}

::v-deep(.filter-button) {
  padding: 0px !important;
}

::v-deep(.search-checklist) {
  padding-left: 32px !important;
  padding-right: 32px !important;
}
</style>
